import React, { useState, useMemo } from "react";
import { formatNaira } from "../../../../utils/dashboardFormat";
import { Notice, PanelHead, StatusPill, formatDay, titleCase } from "../../../../components/MemberProfile/parts";
import { flattenRepayments } from "../../../../components/MemberProfile/profileModel";
import {
  useGetMemberRepaymentHistoryQuery,
} from "../../../../store/member/member-loan";
import TableSkeletonLoader from "../../../../components/Common/TableSkeletonLoader";

const RepaymentHistoryTab = ({ repaymentHistory = {}, totalsByStatus = {}, memberId = null, isMemberContext = false }) => {
  const [filter, setFilter] = useState("all");

  // Fetch comprehensive repayment history from backend (all calculations done server-side)
  const shouldFetchHistory = isMemberContext && (!repaymentHistory || Object.keys(repaymentHistory).length === 0);
  
  const {
    data: repaymentHistoryData,
    isLoading,
    isFetching,
  } = useGetMemberRepaymentHistoryQuery(
    undefined,
    { skip: !shouldFetchHistory }
  );

  // Use data from API if available, otherwise use props
  const combinedRepaymentHistory = useMemo(() => {
    if (repaymentHistoryData?.breakdown) {
      return repaymentHistoryData.breakdown;
    }
    return repaymentHistory || { overdue: [], pending: [], paid: [] };
  }, [repaymentHistory, repaymentHistoryData]);

  const combinedTotalsByStatus = useMemo(() => {
    if (repaymentHistoryData?.totals_by_status) {
      return repaymentHistoryData.totals_by_status;
    }
    return totalsByStatus || { overdue: 0, pending: 0, paid: 0 };
  }, [totalsByStatus, repaymentHistoryData]);

  const isLoadingData = isLoading || isFetching;

  const rows = useMemo(() => flattenRepayments(combinedRepaymentHistory), [combinedRepaymentHistory]);
  const money = (key) => Number(combinedTotalsByStatus?.[key]) || 0;
  const knownTotal = money("overdue") + money("pending") + money("paid");

  if (isLoadingData) {
    return (
      <div className="py-5" aria-busy="true">
        <TableSkeletonLoader rows={3} columns={1} />
      </div>
    );
  }

  if (rows.length === 0 && knownTotal === 0) {
    return <Notice title="No repayments yet">Repayment instalments appear here once this member has a disbursed loan.</Notice>;
  }
  if (rows.length === 0) {
    return (
      <Notice tone="warn" title="Couldn't load the repayment schedule" actionLabel="Reload" onAction={() => window.location.reload()}>
        This member has {formatNaira(knownTotal)} in repayments on record, but the instalments didn't come through.
      </Notice>
    );
  }

  const FILTERS = [
    { key: "all", label: "All", count: rows.length },
    { key: "overdue", label: "Overdue", count: rows.filter((r) => r.status === "overdue").length },
    { key: "pending", label: "Upcoming", count: rows.filter((r) => r.status === "pending").length },
    { key: "paid", label: "Paid", count: rows.filter((r) => r.status === "paid").length },
  ];
  const shown = filter === "all" ? rows : rows.filter((r) => r.status === filter);

  return (
    <div>
      <PanelHead
        title="Repayments"
        stats={[
          { label: "Overdue", value: formatNaira(money("overdue")), tone: "red", quiet: money("overdue") === 0 },
          { label: "Upcoming", value: formatNaira(money("pending")), tone: "amber", quiet: money("pending") === 0 },
          { label: "Paid", value: formatNaira(money("paid")), tone: "green", quiet: money("paid") === 0 },
        ]}
      />
      <div className="mp-filters" role="group" aria-label="Filter repayments by status">
        {FILTERS.filter((f) => f.key === "all" || f.count > 0).map((f) => (
          <button key={f.key} type="button" className={`mp-chip ${filter === f.key ? "on" : ""}`} aria-pressed={filter === f.key} onClick={() => setFilter(f.key)}>
            {f.label} {f.count}
          </button>
        ))}
      </div>
      <div className="mp-scroll">
        <table className="mp-table">
          <thead>
            <tr>
              <th>Loan</th>
              <th>Due</th>
              <th className="num">Principal</th>
              <th className="num">Interest</th>
              <th className="num">Total</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((r, i) => (
              <tr key={r.id ?? i} className={r.status === "overdue" ? "hot" : undefined}>
                <td>
                  {titleCase(r.loanType)}
                  {r.status === "paid" && r.method ? <span className="sub">Paid by {titleCase(r.method)}</span> : null}
                </td>
                <td>
                  {r.status === "paid" ? formatDay(r.paidAt) : formatDay(r.dueAt)}
                  <span className="sub">{r.status === "paid" ? "Paid" : "Due"}</span>
                </td>
                <td className="num">{formatNaira(r.amount)}</td>
                <td className="num">{formatNaira(r.interest)}</td>
                <td className="num">{formatNaira(r.total)}</td>
                <td>
                  <StatusPill status={r.status}>
                    {r.status === "overdue" && r.daysOverdue > 0 ? `Overdue ${r.daysOverdue}d` : undefined}
                  </StatusPill>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default RepaymentHistoryTab;
